import StickyFooter from '@/components/v1/StickyFooter'
import { CheckCircleIcon, StarIcon } from '@heroicons/react/24/solid'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'

const TICK_MS = 40
const STARS = [0, 1, 2, 3, 4]
const TESTIMONIALS = ['t1', 't2', 't3']
const CHECKLIST = ['goals', 'topics', 'schedule', 'plan'] as const
const ROW_SHARE = 100 / CHECKLIST.length

/**
 * Questions asked while the bar is moving, positioned part-way through a row
 * so they interrupt visible work rather than a pause between rows.
 */
const POPUPS = [
  { at: ROW_SHARE * 1.49, id: 'finishWhatYouStart' },
  { at: ROW_SHARE * 2.62, id: 'speakWithoutRehearsing' },
] as const

interface Props {
  durationMs: number
  titleKey: string
}

/**
 * li-funnel-redesign v1 — the step that builds the plan before sign-up.
 *
 * Deliberately a slow screen. The previous version was a 1.5s flash of a
 * single bar, which spends the moment just before the account wall on nothing.
 * Here the same wait itemises the work as four checks that resolve one by one,
 * carries store ratings and reviews while the user has no choice but to look
 * at them, and ends on a button rather than auto-advancing, so continuing is
 * the user's own act.
 *
 * Progress halts entirely while a popup is open — `activePopup` is a dependency
 * of the interval effect, so the timer is torn down and rebuilt around it.
 * Without that the bar would run on behind the overlay and the question would
 * read as decorative.
 */
export default function LoaderStep({ durationMs, titleKey }: Props) {
  const [progress, setProgress] = useState(0)
  const [activePopup, setActivePopup] = useState<string>()
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const answeredPopups = useRef(new Set<string>())
  const progressRef = useRef(0)
  const { t } = useTranslation()

  const isComplete = progress >= 100

  useEffect(() => {
    if (activePopup) return

    const interval = setInterval(() => {
      const current = progressRef.current
      const next = Math.min(current + (100 * TICK_MS) / durationMs, 100)
      const popup = POPUPS.find(
        (question) =>
          current < question.at &&
          next >= question.at &&
          !answeredPopups.current.has(question.id),
      )

      // Parked exactly on the question's mark, so the bar resumes from where
      // it stopped instead of jumping the distance it covered while asking.
      progressRef.current = popup ? popup.at : next
      setProgress(progressRef.current)

      if (popup) setActivePopup(popup.id)
      if (progressRef.current >= 100) clearInterval(interval)
    }, TICK_MS)

    return () => clearInterval(interval)
  }, [activePopup, durationMs])

  const answerPopup = (answer: 'no' | 'yes') => {
    if (!activePopup) return

    answeredPopups.current.add(activePopup)
    setAnswers((current) => ({ ...current, [activePopup]: answer }))
    setActivePopup(undefined)
  }

  return (
    <div className="flex grow flex-col gap-6 pt-2">
      <h1 className="hw-heading text-left!" key={isComplete ? 'done' : 'load'}>
        {t(
          isComplete ? 'pages.onboarding.v1.loader.completeHeading' : titleKey,
        )}
      </h1>

      <ul className="divide-hw-line divide-y">
        {CHECKLIST.map((item, index) => {
          const rowProgress = Math.min(
            Math.max((progress - index * ROW_SHARE) / ROW_SHARE, 0),
            1,
          )
          const isDone = rowProgress >= 1

          // Rows arrive as their turn comes, so the list builds itself rather
          // than showing four things that have obviously not started.
          if (index > 0 && progress < index * ROW_SHARE) return null

          return (
            <li className="animate-fade-up py-3.5" key={item}>
              <div className="flex items-center justify-between gap-3">
                <span className="text-hw-ink text-base font-semibold">
                  {t(
                    `pages.onboarding.v1.loader.${isDone ? 'checklist' : 'checklistActive'}.${item}`,
                  )}
                </span>

                {isDone ? (
                  <CheckCircleIcon className="text-hw-primary animate-check-pop h-6 w-6 shrink-0" />
                ) : (
                  <span className="text-hw-muted shrink-0 text-sm font-semibold tabular-nums">
                    {Math.floor(rowProgress * 100)}%
                  </span>
                )}
              </div>

              {!isDone && (
                <div className="bg-hw-line mt-3 h-1.5 overflow-hidden rounded-full">
                  <div
                    className="bg-hw-primary h-full rounded-full"
                    style={{ width: `${rowProgress * 100}%` }}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {isComplete ? (
        <Form method="post">
          <input
            name={t(titleKey, { lng: 'en' })}
            type="hidden"
            value={t('shared.continue', { lng: 'en' })}
          />

          {/*
            The popup answers ride this submit. Every field in the POST is
            recorded by trackOnboardingFormData, so they land alongside the
            answers from the ordinary question steps with no extra plumbing.
          */}
          {Object.entries(answers).map(([id, answer]) => (
            <input
              key={id}
              name={t(`pages.onboarding.v1.loader.${id}`, { lng: 'en' })}
              type="hidden"
              value={t(`pages.onboarding.v1.loader.${answer}`, { lng: 'en' })}
            />
          ))}

          <StickyFooter>
            <button className="hw-button-primary" type="submit">
              {t('shared.continue')}
            </button>
          </StickyFooter>
        </Form>
      ) : (
        <div className="space-y-4 pt-2 pb-6">
          <div className="flex flex-col items-center gap-1">
            <div className="flex">
              {STARS.map((star) => (
                <StarIcon className="text-hw-yellow h-6 w-6" key={star} />
              ))}
            </div>

            <p className="text-hw-ink text-center text-xl font-bold text-balance">
              {t('pages.onboarding.v1.loader.socialProof')}
            </p>
          </div>

          <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TESTIMONIALS.map((testimonial) => (
              <div
                className="hw-card w-[85%] shrink-0 snap-center p-4"
                key={testimonial}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-hw-ink font-semibold">
                    {t(`pages.checkout.testimonials.${testimonial}.author`)}
                  </p>

                  <div className="flex">
                    {STARS.map((star) => (
                      <StarIcon className="text-hw-yellow h-4 w-4" key={star} />
                    ))}
                  </div>
                </div>

                <p className="text-hw-muted mt-2 text-sm leading-relaxed">
                  {t(`pages.checkout.testimonials.${testimonial}.text`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activePopup &&
        createPortal(
          <div className="bg-hw-ink/40 animate-fade-in fixed inset-0 z-50 flex items-center justify-center px-5">
            <div className="animate-scale-in w-full max-w-sm rounded-2xl bg-white p-5 text-center">
              <p className="text-hw-muted text-sm font-medium">
                {t('pages.onboarding.v1.loader.popupLabel')}
              </p>

              <p className="text-hw-ink mt-1 text-lg font-bold text-balance">
                {t(`pages.onboarding.v1.loader.${activePopup}`)}
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                {(['no', 'yes'] as const).map((answer) => (
                  <button
                    className="bg-hw-fill text-hw-ink hover:bg-hw-line rounded-2xl py-3.5 font-semibold transition-[transform,background-color] active:scale-95"
                    key={answer}
                    onClick={() => answerPopup(answer)}
                    type="button"
                  >
                    {t(`pages.onboarding.v1.loader.${answer}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
